import dateOptions from "@utils/dateOptions";

const millisecondsInMinute = 60 * 1000;
export const APPOINTMENT_DURATION_MINUTES = 25;

// Formats date in the form of: Weekday, Month(short) Day. i.e. Wednesday, March 16th
export const dayOnlyDateOptions: Intl.DateTimeFormatOptions = {
  weekday: "long",
  month: "short",
  day: "numeric",
};

// Formats time in the form of: Hour:Minute AM/PM TZ. i.e. Wednesday, March 16th
export const timeOnlyDateOptions: Intl.DateTimeFormatOptions = {
  hour: "numeric",
  minute: "numeric",
  timeZoneName: "short",
};

// Return a Date object for appointment start, offset by 5 minutes for RPM consults.
// Accepts a date string in UTC.
const getAppointmentStart = (start: string): Date => {
  const actualStartTime = new Date(Date.parse(start));

  return new Date(actualStartTime.getTime());
};

// Return a Date object for the appointment end, given the date string in UTC for the
// appointments start date.
const getAppointmentEnd = (start: string) => {
  const actualStartTime = getAppointmentStart(start);

  // Appoints end after 25 minutes if they are RPM, since they start 5 minutes later to given
  // clinicians time to read notes.
  return new Date(
    actualStartTime.getTime() +
      APPOINTMENT_DURATION_MINUTES * millisecondsInMinute
  );
};

// Helper function to convert dates to locale strings but maintain ability to
// reverse string using Date.parse(). Unwanted behavior occurs when using Long
// month notation that add the word "at" which prevents the whole date from
// being reparsed.
export const getLocaleDateString = (
  date: Date,
  datetimeOptions: Intl.DateTimeFormatOptions
) => {
  return date.toLocaleString("en-US", datetimeOptions).split(" at").join();
};

// Return a locally formatted date string for an appointment start time given a UTC date string and the consult type.
// Optionally pass in additional formatting options.
export const getLocalFormattedStartDate = (
  start: string,
  datetimeOptions = dateOptions
): string => {
  /*
    As a part of HC-278 we started shifting the time we tell RPM patients the
    appointment starts to be five minutes after the actual start time.
  */
  const startTimeToDisplayToUser = getAppointmentStart(start);
  return getLocaleDateString(startTimeToDisplayToUser, datetimeOptions);
};

// Return a locally formatted date string for an appointment end time given a UTC date string and the consult type.
// Optionally pass in additional formatting options.
export const getLocalFormattedEndDate = (
  end: string,
  datetimeOptions = dateOptions
): string => {
  const endTimeToDisplayToUser = getAppointmentEnd(end);
  return getLocaleDateString(endTimeToDisplayToUser, datetimeOptions);
};
